import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useGetGlobalStorefrontSettingsQuery, useGetStorefrontQuery, useGetStorefrontThemesQuery, usePatchStorefrontMutation } from '../../../store/endpoints/StorefrontSettingsEndpoints';
import { IStorefrontSettings, IStorefrontTheme, IStorefrontSection, IStorefrontBanner, IStorefrontPage, ISectionSortOrderUpdate, IStorefrontStyle, buildStorefrontPatch } from '../../../models/Storefront';
import { isEqual, omit } from 'lodash';
import { paletteCssVars } from '../../../components/Storefront/Editor/stylesConfig';
import StorefrontHomePageEditor from '../../../components/Storefront/Editor/StorefrontHomePageEditor';
import StorefrontSearchPageEditor from '../../../components/Storefront/Editor/StorefrontSearchPageEditor';
import StorefrontHeaderFooterEditor from '../../../components/Storefront/Editor/StorefrontHeaderFooterEditor';
import StorefrontCartPageEditor from '../../../components/Storefront/Editor/StorefrontCartPageEditor';
import StorefrontCheckoutPageEditor from '../../../components/Storefront/Editor/StorefrontCheckoutPageEditor';
import StorefrontProductPageEditor from '../../../components/Storefront/Editor/StorefrontProductPageEditor';
import StorefrontBundlePageEditor from '../../../components/Storefront/Editor/StorefrontBundlePageEditor';
import StorefrontLoginPageEditor from '../../../components/Storefront/Editor/Login/StorefrontLoginPageEditor';
import { ContextualMenu, ContextualMenuItemType, DefaultButton, Icon, IContextualMenuItem, Label, Pivot, PivotItem, PrimaryButton, Spinner, SpinnerSize, Stack, StackItem } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { useUrlPanelState, useUrlTabState } from '../../../hooks/useUrlState';
import NumberField from '../../../components/Inputs/NumberField';
import StorefrontThemeSelection from '../../../components/Storefront/StorefrontThemeSelection';
import { useGetStorefrontFooterLinksQuery, useGetStorefrontHeaderLinksQuery } from '../../../store/endpoints/StorefrontLinkEndpoints';
import { useGetStorefrontBundlesQuery, useGetStorefrontCategorySectionPreviewQuery, useGetStorefrontFeaturedProductsQuery } from '../../../store/endpoints/StorefrontProductEndpoints';
import { useGetStorefrontSectionsQuery, usePatchStorefrontSectionsMutation } from '../../../store/endpoints/StorefrontSectionEndpoints';
import { useGetStorefrontBannersQuery, useSaveStorefrontBannersMutation } from '../../../store/endpoints/StorefrontBannerEndpoints';
import { toast } from 'react-toastify';
import StorefrontSettingsTitleBar from '../../../components/Storefront/StorefrontSettingsTitleBar';
import StorefrontManagePagesPanel from '../../../components/Storefront/Editor/StorefrontManagePagesPanel';
import { sortedPages } from '../../../components/Storefront/Editor/pagesConfig';
import { useGetStorefrontPagesQuery, useSaveStorefrontPagesMutation, useDeleteStorefrontPageMutation } from '../../../store/endpoints/StorefrontPageEndpoints';
import { useGetStorefrontStylesQuery } from '../../../store/endpoints/StorefrontStyleEndpoints';
import StorefrontStylesPanel from '../../../components/Storefront/Editor/StorefrontStylesPanel';
import { StorefrontStylesProvider } from '../../../components/Storefront/Editor/StorefrontStylesContext';
import { AssetSourceProvider } from '../../../components/Storefront/Assets/AssetSourceContext';
import { applyStyleToAll, planStyleBulkApply } from '../../../components/Storefront/Editor/styleBulkApply';
import { getDefaultStyleIds, setDefaultStyleId } from '../../../components/Storefront/Editor/styleConfig';
import useCan, { STOREFRONT_ADMIN } from '../../../permissions/ability';
import useStorefrontEditLease from '../../../hooks/useStorefrontEditLease';
import { StorefrontReadOnlyRegion } from '../../../components/Storefront/StorefrontEditLock';
import { storefrontSaveErrorMessage } from '../../../logic/storefrontLeaseConflict';

interface StorefrontEditorPageParams {
    storefront: string;
}

// Fields the in-Edit-Mode Home editor owns (hero presentation + per-page banner height). Everything
// else on a page — title/slug/enabled/sortOrder — is owned by the Manage Pages panel, which commits
// those immediately. On Exit Edit Mode we rebase each dirty snapshot onto the latest server page and
// carry over ONLY these fields, so a rename/slug/enable committed via the panel this session isn't
// reverted by the stale identity frozen into the snapshot.
// Pull a human-readable reason out of whatever the failed save rejected with: an RTK Query
// FetchBaseQueryError ({ status, data }) whose data is usually an ActionResponse { message },
// a plain Error (the pages batch throws one for HTTP-200 success:false responses), or anything else.
const extractSaveErrorDetail = (e: unknown): string | undefined => {
    if (e instanceof Error) return e.message || undefined;
    if (typeof e === 'object' && e !== null) {
        const err = e as { status?: unknown; data?: { message?: unknown; title?: unknown } };
        const message = err.data?.message ?? err.data?.title;
        if (typeof message === 'string' && message) return message;
        if (err.status !== undefined) return `request failed (${String(err.status)})`;
    }
    return undefined;
};

const PAGE_METADATA_KEYS: (keyof IStorefrontPage)[] = [
    'bannerHeight',
    'bannerFullWidth',
    'bannerImageFit',
    'bannerUnderHeader',
    'bannerUnderHeaderBackground',
    'bannerUnderHeaderShadow',
];

const StorefrontPagesEditorPage: React.FC<RouteComponentProps<StorefrontEditorPageParams>> = (props) => {
    const { storefront } = props.match.params;
    const gSettings = useGetGlobalStorefrontSettingsQuery();
    const storefrontQuery = useGetStorefrontQuery(storefront, { skip: !storefront });
    const [sSettings, setSSettings] = React.useState<IStorefrontSettings | undefined>(undefined);
    const originalSettingsRef = React.useRef<IStorefrontSettings | undefined>();
    const themes = useGetStorefrontThemesQuery();
    const [gTheme, setGTheme] = React.useState<IStorefrontTheme>();
    const [sTheme, setSTheme] = React.useState<IStorefrontTheme>();
    const [editMode, setEditMode] = React.useState(false);
    /*
     * A settings deep link into this editor turns Edit Mode on.
     *
     * Several controls here only render in Edit Mode - the banner hero options (image fit, full
     * width, under header), the in-place style pickers - so a link to one of those would otherwise
     * land on a page where the setting genuinely is not present, and report exactly that.
     *
     * Only ENTERING is automatic. Leaving is what commits the draft (see the Exit Edit Mode path
     * above), so that stays a deliberate act by the person editing.
     */
    React.useEffect(() => {
        if (new URLSearchParams(window.location.search).get('focus')) {
            setEditMode(true);
        }
    }, []);
    const [saving, setSaving] = React.useState(false);
    const [patchStorefront] = usePatchStorefrontMutation();
    const [patchStorefrontSections] = usePatchStorefrontSectionsMutation();
    const [saveStorefrontBanners] = useSaveStorefrontBannersMutation();
    // Custom pages are a first-class API resource now. The list drives the page-switcher menu + the
    // Home editor; CRUD is immediate (manage panel), except per-page hero/banner-height metadata
    // edited inside Edit Mode, which is dirty-tracked here and batch-upserted on Exit Edit Mode.
    const sPages = useGetStorefrontPagesQuery(storefront, { skip: !storefront });
    const [saveStorefrontPages] = useSaveStorefrontPagesMutation();
    const [deleteStorefrontPage] = useDeleteStorefrontPageMutation();
    const [dirtyPages, setDirtyPages] = React.useState<Record<string, IStorefrontPage>>({});
    // Full banner list (ALL pages), owned here so page-delete cleanup and the batch-save full-list
    // preservation live in one place (symmetric with `sections`). The batch endpoint deletes every
    // banner then re-adds the posted list, so we must always submit the complete list.
    const sBanners = useGetStorefrontBannersQuery(storefront, { skip: !storefront });
    const [banners, setBanners] = React.useState<IStorefrontBanner[]>([]);
    // The Home tab's editing context: undefined = Main Homepage, else a custom page GUID.
    const [selectedPageId, setSelectedPageId] = React.useState<string | undefined>(undefined);
    // The Home pivot tab doubles as a page switcher: its chevron opens a menu of home pages
    // (Main Homepage + custom pages) plus "Manage Pages…", which opens the management panel.
    // Driven by ?tab= so another screen can deep-link a page editor (the Authentication settings
    // page sends admins straight to 'login'), and so the tab stays in the URL as the user moves.
    const [activeTab, setActiveTab] = useUrlTabState('home');
    const [pagesMenuOpen, setPagesMenuOpen] = React.useState(false);
    // In the URL (?panel=managePages) so documentation can link straight to a custom page's title,
    // slug or enabled state.
    const managePagesPanel = useUrlPanelState('managePages');
    const homeChevronId = useId('home-pages-chevron');
    const sfHeaderLinks = useGetStorefrontHeaderLinksQuery({ storefrontId: storefront }, { skip: !storefront });
    const globalHeaderLinks = useGetStorefrontHeaderLinksQuery({ storefrontId: 'global' });
    const sfFooterLinks = useGetStorefrontFooterLinksQuery({ storefrontId: storefront }, { skip: !storefront });
    const globalFooterLinks = useGetStorefrontFooterLinksQuery({ storefrontId: 'global' });
    const sSections = useGetStorefrontSectionsQuery(storefront, { skip: !storefront });
    const [sections, setSections] = React.useState<IStorefrontSection[]>([]);
    // Publish, Revert and Apply Template all change the server's copy of this storefront underneath the
    // editor. Sections and banners already re-seed from their queries when the data changes, but the settings
    // record is seeded exactly ONCE (see the originalSettingsRef guard below), so after any of those actions
    // the editor went on rendering the theme, colors, fonts and every *Config blob it loaded with — the toast
    // said it worked and the screen said it had not. Clearing the ref lets that effect run again on the
    // refetched data.
    //
    // Only on an explicit action, never on an arbitrary refetch: re-seeding while someone is mid-edit would
    // discard their unsaved work, and these three are the moments where the server's copy is the one that
    // should win.
    const noop = React.useCallback(() => undefined, []);
    const refreshFromServer = React.useCallback(() => {
        originalSettingsRef.current = undefined;
    }, []);

    // ── Edit lease ───────────────────────────────────────────────────────────────────────
    // While an assistant turn holds this storefront, every write here would come back 409. This page
    // already has exactly the flag that turns editing off everywhere: `editMode` is what puts the
    // pencils, drag handles and popovers on the page, so suspending it is the whole read-only story
    // for the seven editors below. It is folded in rather than assigned so a lock never destroys the
    // user's own edit-mode intent: local working state (sSettings/sections/banners) is untouched, and
    // when the turn ends the page comes straight back to where they were, changes intact.
    const editLease = useStorefrontEditLease(storefront);
    const readOnly = editLease.locked;
    const effectiveEditMode = editMode && !readOnly;

    // ── Storefront Styles ────────────────────────────────────────────────────────────────
    // Named, reusable element style presets (own storefront ∪ org globals). Elements reference
    // them by id through a `$styleRef` key inside their style dict; the resolution happens in the
    // preview style builders (Editor/styleControls.tsx) against whatever list the provider below
    // publishes. Merging the Styles panel's in-progress draft into that list is what makes an
    // unsaved style tweak re-render every element on the page that uses it.
    // The list itself only needs storefront read (everyone who can open this editor) — the preview
    // has to resolve refs for viewers too. Only the panel and its mutations are admin-gated, matching
    // the API's own split (GET = STOREFRONT_READ, mutations = STOREFRONT_ADMIN).
    const canAdminStyles = useCan(STOREFRONT_ADMIN);
    const sStyles = useGetStorefrontStylesQuery(storefront, { skip: !storefront });
    const [stylesPanelOpen, setStylesPanelOpen] = React.useState(false);
    const [draftStyle, setDraftStyle] = React.useState<IStorefrontStyle | undefined>();
    const serverStyles = React.useMemo(() => sStyles.data?.items ?? [], [sStyles.data]);
    const effectiveStyles = React.useMemo(() => {
        if (!draftStyle) return serverStyles;
        // A just-created style may not be in the refetched list yet — append rather than drop it.
        return serverStyles.some((s) => s.id === draftStyle.id)
            ? serverStyles.map((s) => (s.id === draftStyle.id ? draftStyle : s))
            : [...serverStyles, draftStyle];
    }, [serverStyles, draftStyle]);
    // Delete's "used by N elements" count scans the working config blobs + sections for the id.
    const styleUsageRoots = React.useMemo(() => [sSettings, sections], [sSettings, sections]);

    // The active palette/font pack published as :root CSS custom properties, exactly like the
    // shopper renderer's Layout does. Preview surfaces authored against the vars (the animated
    // page-background wash, customCSS rules) repaint here the moment the active palette changes.
    React.useEffect(() => {
        const css = paletteCssVars(effectiveStyles);
        if (!css) return;
        const style = document.createElement('style');
        style.setAttribute('data-sf-palette', '');
        style.innerHTML = css;
        document.head.appendChild(style);
        return () => { document.head.removeChild(style); };
    }, [effectiveStyles]);
    // "Apply to all …": the panel describes the change through `planStyleBulkApply` and performs it
    // through this callback, which rewrites the page-config blobs on the WORKING settings object.
    // The page already owns that state (`sSettings` / `setSSettings`) and already persists it on
    // Exit Edit Mode via buildStorefrontPatch, so a bulk apply rides the same draft/save path as any
    // other config edit — the panel makes no API call of its own.
    const applyStyleToAllElements = React.useCallback((style: IStorefrontStyle): number => {
        if (!sSettings) return 0;
        const { settings, count } = applyStyleToAll(sSettings, style);
        if (count > 0) setSSettings(settings);
        return count;
    }, [sSettings]);

    // Real product/bundle data for the page previews (non-blocking — editors fall back to sample
    // data until these load or when a storefront has none). Featured products are the primary
    // source; a general category-section preview backfills stores that feature nothing.
    const featuredProducts = useGetStorefrontFeaturedProductsQuery({ id: storefront, maxEntries: 8 }, { skip: !storefront });
    const categoryProducts = useGetStorefrontCategorySectionPreviewQuery({ id: storefront, maxEntries: 8 }, { skip: !storefront });
    const storefrontBundles = useGetStorefrontBundlesQuery({ id: storefront, maxEntries: 4 }, { skip: !storefront });
    const sampleProducts = React.useMemo(() => {
        const featured = featuredProducts.data?.items ?? [];
        const merged = [...featured];
        const seen = new Set(featured.map((p) => p.id));
        for (const p of categoryProducts.data?.items ?? []) {
            if (!seen.has(p.id)) { merged.push(p); seen.add(p.id); }
        }
        return merged;
    }, [featuredProducts.data, categoryProducts.data]);
    const sampleBundles = storefrontBundles.data?.items ?? [];

    const doSave = React.useCallback(async (updated: IStorefrontSettings) => {
        if (!originalSettingsRef.current) return;
        const patch = buildStorefrontPatch(originalSettingsRef.current, updated);
        if (Object.keys(patch).length === 0) return;
        try {
            await patchStorefront({ id: storefront, patch }).unwrap();
            originalSettingsRef.current = { ...originalSettingsRef.current, ...patch };
        } catch (e) {
            toast.error(storefrontSaveErrorMessage(e, 'Failed to save settings'));
        }
    }, [patchStorefront, storefront]);

    // Delete a custom page. The server cascades: the page row AND every section + banner belonging
    // to it are deleted in one call (and the delete mutation invalidates the sections cache). We ALSO
    // drop the deleted page's sections/banners from the working lists so the next Exit-Edit-Mode batch
    // save doesn't resurrect them (the sections PATCH delta and banners full-list PUT both submit from
    // working state). Filtered by their `page` field. If the deleted page was selected, fall back to
    // the main homepage.
    const handleDeletePage = React.useCallback(async (pageId: string) => {
        const page = (sPages.data ?? []).find((p) => p.id === pageId);
        const name = page?.title || page?.slug || 'page';
        const toastId = toast.loading(`Deleting ${name}…`);
        try {
            const result = await deleteStorefrontPage({ storefront, pageId }).unwrap();
            if (result.success === false) {
                toast.update(toastId, { type: 'error', render: result.message || `Failed to delete ${name}`, isLoading: false, autoClose: 4000 });
                return;
            }
            setSections((prev) => prev.filter((s) => s.page !== pageId));
            setBanners((prev) => prev.filter((b) => b.page !== pageId));
            setDirtyPages((prev) => { const next = { ...prev }; delete next[pageId]; return next; });
            if (selectedPageId === pageId) setSelectedPageId(undefined);
            toast.update(toastId, { type: 'success', render: `${name} deleted`, isLoading: false, autoClose: 3000 });
        } catch {
            toast.update(toastId, { type: 'error', render: `Failed to delete ${name}`, isLoading: false, autoClose: 4000 });
        }
    }, [deleteStorefrontPage, storefront, sPages.data, selectedPageId]);

    // Per-page metadata edited inside Edit Mode (hero options / banner height) via the Home editor —
    // dirty-tracked, batch-upserted on Exit Edit Mode alongside the sections + banners saves.
    const handlePageMetadataChange = React.useCallback((page: IStorefrontPage) => {
        if (!page.id) return;
        setDirtyPages((prev) => ({ ...prev, [page.id as string]: page }));
    }, []);

    React.useEffect(() => {
        if (!themes.data) return;
        if (gSettings.data?.theme && themes.data[gSettings.data.theme]) {
            setGTheme(themes.data[gSettings.data.theme]);
        }
    }, [themes.data, gSettings.data]);

    React.useEffect(() => {
        if (sSections.data) {
            const sorted = [...sSections.data.items].sort((a, b) => (a.sortOrder ?? 100) - (b.sortOrder ?? 100));
            setSections(sorted);
        }
    }, [sSections.data]);

    React.useEffect(() => {
        if (sBanners.data) {
            const sorted = [...sBanners.data.items].sort((a, b) => (a.sortOrder ?? 100) - (b.sortOrder ?? 100));
            setBanners(sorted);
        }
    }, [sBanners.data]);

    React.useEffect(() => {
        if (!themes.data) return;
        if (sSettings?.theme && themes.data[sSettings.theme]) {
            setSTheme(themes.data[sSettings.theme]);
        }
    }, [themes.data, sSettings?.theme]);

    React.useEffect(() => {
        if (storefrontQuery.data && !originalSettingsRef.current) {
            setSSettings(storefrontQuery.data);
            originalSettingsRef.current = storefrontQuery.data;
        }
    }, [storefrontQuery.data]);

    if (gSettings.isLoading || storefrontQuery.isLoading || themes.isLoading || sSections.isLoading || sBanners.isLoading || sPages.isLoading) {
        return <div style={{ marginTop: '5em' }}><Spinner size={SpinnerSize.large} label='Loading' styles={{ circle: { width: '100px', height: '100px', borderWidth: '8px' } }} /></div>;
    }

    if (!sSettings || !gSettings.data) {
        return <div>Storefront not found</div>;
    }

    // Custom pages for the Home tab's page-switcher menu. The selected page's title shows in the
    // tab itself so the editing context stays visible with the old "Editing page" dropdown gone.
    const customPages = sortedPages(sPages.data ?? []);
    const selectedPage = customPages.find((p) => p.id === selectedPageId);
    // The Home editor sees the pages merged with any in-edit dirty metadata (hero/banner-height),
    // so its previews reflect unsaved edits until the Exit-Edit-Mode batch upsert persists them.
    const mergedPages = customPages.map((p) => (p.id && dirtyPages[p.id]) ? dirtyPages[p.id] : p);
    const selectHomePage = (pageId?: string) => {
        setSelectedPageId(pageId);
        setActiveTab('home');
    };
    const pagesMenuItems: IContextualMenuItem[] = [
        { key: '__main__', text: 'Main Homepage', canCheck: true, checked: !selectedPageId, onClick: () => selectHomePage(undefined) },
        ...customPages.map((p) => ({
            key: p.id ?? '',
            text: `${p.title || p.slug} (/${p.slug})${p.enabled === false ? ' — disabled' : ''}`,
            canCheck: true,
            checked: selectedPageId === p.id,
            onClick: () => selectHomePage(p.id),
        })),
        { key: 'manageDivider', itemType: ContextualMenuItemType.Divider },
        {
            key: 'manage',
            text: 'Manage Pages…',
            iconProps: { iconName: 'Settings' },
            // The menu renders in a Fluent layer, outside the read-only region's DOM subtree, so it has
            // to consult the folded flag itself.
            disabled: !effectiveEditMode,
            title: effectiveEditMode ? undefined : 'Enter Edit Mode to manage pages',
            onClick: () => managePagesPanel.open(),
        },
    ];

    const handleEditModeToggle = async () => {
        if (editMode) {
            if (saving) return;
            setSaving(true);
            try {
                const savePromises: Promise<unknown>[] = [];

                if (originalSettingsRef.current) {
                    const patch = buildStorefrontPatch(originalSettingsRef.current, sSettings);
                    if (Object.keys(patch).length > 0) {
                        savePromises.push(patchStorefront({ id: storefront, patch }).unwrap());
                    }
                }

                // Delta save: only changed sections travel to the PATCH endpoint (omission never
                // deletes there, unlike the legacy batch endpoint). Baseline = the last-loaded server
                // state; target sortOrder for each section is its current index i (what the old
                // full-list save persisted).
                const baseline = sSections.data?.items ?? [];
                const baselineById = new Map(baseline.map((s) => [s.id, s]));
                // Compare only content, ignoring order/new-flag — sortOrder moves are handled separately.
                const contentOf = (s: IStorefrontSection) => omit(s, ['sortOrder', 'isNew']);

                const upserts: IStorefrontSection[] = [];
                const sortOrders: ISectionSortOrderUpdate[] = [];
                sections.forEach((s, i) => {
                    const { isNew, ...rest } = s;
                    const base = s.id ? baselineById.get(s.id) : undefined;
                    if (isNew || !base) {
                        // New section (or an existing-id section the server never sent) → full upsert.
                        upserts.push({ ...rest, sortOrder: i, id: isNew ? undefined : s.id });
                    } else if (!isEqual(contentOf(s), contentOf(base))) {
                        // Content changed → full upsert (carries the new sortOrder too).
                        upserts.push({ ...rest, sortOrder: i });
                    } else if (base.sortOrder !== i) {
                        // Only the position moved (or a legacy null sortOrder needs normalizing —
                        // the load sort coalesces null to 100, so leaving it null risks a position
                        // jump on the next load) → order-only update.
                        sortOrders.push({ id: s.id as string, sortOrder: i });
                    }
                });
                // Baseline sections no longer present in the working list → explicit deletes.
                const currentIds = new Set(sections.map((s) => s.id).filter(Boolean));
                const deletes = baseline
                    .map((s) => s.id)
                    .filter((id): id is string => !!id && !currentIds.has(id));

                if (upserts.length > 0 || deletes.length > 0 || sortOrders.length > 0) {
                    savePromises.push(patchStorefrontSections({ delta: { upserts, deletes, sortOrders }, storefront }).unwrap());
                }

                // Post the FULL banner list (all pages). The batch endpoint deletes all then re-adds
                // exactly this list, so hidden pages' banners must be present here (they are — `banners`
                // is never sliced by page; only the editor's DISPLAY filters by page).
                const bannersToSave = banners.map((b, i) => ({
                    ...b,
                    sortOrder: i,
                    id: b.id?.startsWith('new-') ? undefined : b.id,
                }));
                savePromises.push(saveStorefrontBanners({ banners: bannersToSave, storefront }).unwrap());

                // Per-page metadata (hero options / banner height) edited inside Edit Mode via the
                // Home editor — batch-upsert the dirty set (upsert-only; no implicit deletes). Each
                // dirty snapshot froze the WHOLE page at edit time, including title/slug/enabled that
                // the Manage Pages panel may have changed (and persisted) afterward. Rebase each onto
                // the latest server page (sPages.data refetched after the panel's immediate saves) and
                // carry over only the editor-owned metadata fields, so those panel edits aren't reverted.
                const latestById = new Map((sPages.data ?? []).map((p) => [p.id, p]));
                const dirty = Object.values(dirtyPages).map((p) => {
                    const latest = p.id ? latestById.get(p.id) : undefined;
                    if (!latest) return p;
                    const rebased: IStorefrontPage = { ...latest };
                    const src = p as unknown as Record<string, unknown>;
                    const dst = rebased as unknown as Record<string, unknown>;
                    for (const k of PAGE_METADATA_KEYS) dst[k] = src[k];
                    return rebased;
                });
                if (dirty.length > 0) {
                    // The batch endpoint reports validation failures as success:false on HTTP 200 —
                    // surface those as a save failure so the dirty set isn't cleared silently.
                    savePromises.push(saveStorefrontPages({ pages: dirty, storefront }).unwrap().then((r) => {
                        if (r.success === false) throw new Error(r.message);
                    }));
                }

                await Promise.all(savePromises);
                if (originalSettingsRef.current) {
                    originalSettingsRef.current = { ...originalSettingsRef.current, ...buildStorefrontPatch(originalSettingsRef.current, sSettings) };
                }
                setDirtyPages({});
                toast.success('Storefront saved successfully');
            } catch (e) {
                console.error('Storefront save failed', e);
                // A save started just before the assistant took the lease comes back 409. That is not a
                // failure the user caused or can fix by reading a server detail, so it gets the plain
                // "try again in a moment" instead of the diagnostic wording.
                const detail = extractSaveErrorDetail(e);
                toast.error(storefrontSaveErrorMessage(e, `Failed to save storefront${detail ? `: ${detail}` : ''}`));
            } finally {
                setSaving(false);
            }
            setEditMode(false);
        } else {
            setEditMode(true);
        }
    };

    return (
        <AssetSourceProvider value={{ storefrontId: storefront }}>
        <StorefrontStylesProvider styles={effectiveStyles} storefrontId={storefront} canEdit={canAdminStyles}>
        <div>
            <StorefrontSettingsTitleBar
                storefront={storefront}
                title='Storefront Pages'
                onSettingsChanged={refreshFromServer}
             />
            {/* The controls between here and the page tabs are off while the lease is held. The two
                "Global Settings" controls save the moment they change (no Edit Mode to suspend), and
                the Edit Mode / Styles buttons would start work that could not be saved. The title bar
                stays outside: previewing a locked storefront is fine, and it carries the explanation. */}
            <StorefrontReadOnlyRegion readOnly={readOnly}>
            <Label>Global Settings</Label>
            <Stack horizontal tokens={{ childrenGap: '1em' }} style={{ marginBottom: '1em' }}>
                <StackItem grow>
                    <NumberField settingId='storefront.pages.maxWidth' label='Max Content Width (pixels)' value={sSettings.maxWidth} onChange={(value) => {
                        const updated = { ...sSettings, maxWidth: value };
                        setSSettings(updated);
                        doSave(updated);
                    }} />
                    <StorefrontThemeSelection theme={sSettings.theme ?? gSettings.data.theme} onChange={(theme) => {
                        const updated = { ...sSettings, theme };
                        setSSettings(updated);
                        doSave(updated);
                    }} />
                    {/* "Swap to second image on hover" moved into the Search page editor's
                        Image modal (Storefront > Pages > Search > Image…). */}
                </StackItem>
            </Stack>
            <Stack horizontal horizontalAlign='start' tokens={{ childrenGap: '0.5em' }} style={{ marginBottom: 12 }}>
                <PrimaryButton
                    onClick={handleEditModeToggle}
                    disabled={saving || readOnly}
                    title={readOnly ? 'Paused while changes are being applied to this storefront' : undefined}
                    iconProps={{ iconName: effectiveEditMode ? 'Save' : 'Edit' }}
                >
                    {effectiveEditMode ? (saving ? 'Saving...' : 'Exit Edit Mode') : 'Enter Edit Mode'}
                </PrimaryButton>
                {/* Storefront Styles — the named-style rail. Edit Mode only (the pickers it feeds
                    only appear in the pencil popovers), and gated on the same permission the API
                    requires to mutate styles. */}
                {canAdminStyles && (
                    <DefaultButton
                        onClick={() => setStylesPanelOpen(true)}
                        disabled={!effectiveEditMode || saving}
                        title={effectiveEditMode ? 'Manage named element styles' : 'Enter Edit Mode to manage styles'}
                        iconProps={{ iconName: 'Brush' }}
                    >
                        Storefront Styles
                    </DefaultButton>
                )}
            </Stack>
            </StorefrontReadOnlyRegion>
            {/* The page tabs stay live while the lease is held. Browsing between Header, Home, Search and
                the rest is a read, and watching the assistant work is the reason the dock panel exists.
                The editors below are already read-only: every one of them is handed effectiveEditMode,
                which folds in the lease, so a disabled fieldset here would only cost tab navigation. */}
            <Pivot selectedKey={activeTab} onLinkClick={(item) => { if (item?.props.itemKey) setActiveTab(item.props.itemKey); }}>
                <PivotItem
                    itemKey='home'
                    headerText={selectedPage ? `Home: ${selectedPage.title || selectedPage.slug}` : 'Home'}
                    onRenderItemLink={(linkProps, defaultRender) => (
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            {defaultRender?.(linkProps)}
                            {/* Chevron opens the page-switcher menu; stopPropagation keeps the click
                                from also registering as a tab selection. */}
                            <Icon
                                id={homeChevronId}
                                iconName='ChevronDown'
                                title='Switch home page'
                                style={{ fontSize: 10, padding: '0 6px', cursor: 'pointer' }}
                                onClick={(e) => { e.stopPropagation(); setPagesMenuOpen(true); }}
                            />
                        </span>
                    )}
                >
                    <StorefrontHomePageEditor
                        globalSettings={gSettings.data}
                        globalTheme={gTheme}
                        storefrontSettings={sSettings}
                        storefrontTheme={sTheme}
                        editMode={effectiveEditMode}
                        selectedPageId={selectedPageId}
                        pages={mergedPages}
                        onPageMetadataChange={handlePageMetadataChange}
                        onStorefrontSettingsChange={(newSettings) => setSSettings(newSettings)}
                        storefrontHeaderLinks={sfHeaderLinks.data?.items ?? []}
                        globalHeaderLinks={globalHeaderLinks.data?.items ?? []}
                        onHeaderLinksChange={noop}
                        storefrontFooterLinks={sfFooterLinks.data?.items ?? []}
                        globalFooterLinks={globalFooterLinks.data?.items ?? []}
                        onFooterLinksChange={noop}
                        banners={banners}
                        onBannersChange={setBanners}
                        sections={sections}
                        onSectionsChange={(newSections) => setSections(newSections)}
                    />
                </PivotItem>
                <PivotItem itemKey='search' headerText='Search'>
                    <StorefrontSearchPageEditor
                        globalSettings={gSettings.data}
                        globalTheme={gTheme}
                        storefrontSettings={sSettings}
                        storefrontTheme={sTheme}
                        editMode={effectiveEditMode}
                        onStorefrontSettingsChange={(newSettings) => setSSettings(newSettings)}
                        storefrontHeaderLinks={sfHeaderLinks.data?.items ?? []}
                        globalHeaderLinks={globalHeaderLinks.data?.items ?? []}
                        onHeaderLinksChange={noop}
                        storefrontFooterLinks={sfFooterLinks.data?.items ?? []}
                        globalFooterLinks={globalFooterLinks.data?.items ?? []}
                        onFooterLinksChange={noop}
                    />
                </PivotItem>
                <PivotItem itemKey='product' headerText='Product'>
                    <StorefrontProductPageEditor
                        globalSettings={gSettings.data}
                        globalTheme={gTheme}
                        storefrontSettings={sSettings}
                        storefrontTheme={sTheme}
                        editMode={effectiveEditMode}
                        onStorefrontSettingsChange={(newSettings) => setSSettings(newSettings)}
                        storefrontHeaderLinks={sfHeaderLinks.data?.items ?? []}
                        globalHeaderLinks={globalHeaderLinks.data?.items ?? []}
                        onHeaderLinksChange={noop}
                        storefrontFooterLinks={sfFooterLinks.data?.items ?? []}
                        globalFooterLinks={globalFooterLinks.data?.items ?? []}
                        onFooterLinksChange={noop}
                        sampleProducts={sampleProducts}
                        sampleBundles={sampleBundles}
                    />
                </PivotItem>
                <PivotItem itemKey='bundle' headerText='Bundle'>
                    <StorefrontBundlePageEditor
                        globalSettings={gSettings.data}
                        globalTheme={gTheme}
                        storefrontSettings={sSettings}
                        storefrontTheme={sTheme}
                        editMode={effectiveEditMode}
                        onStorefrontSettingsChange={(newSettings) => setSSettings(newSettings)}
                        storefrontHeaderLinks={sfHeaderLinks.data?.items ?? []}
                        globalHeaderLinks={globalHeaderLinks.data?.items ?? []}
                        onHeaderLinksChange={noop}
                        storefrontFooterLinks={sfFooterLinks.data?.items ?? []}
                        globalFooterLinks={globalFooterLinks.data?.items ?? []}
                        onFooterLinksChange={noop}
                        sampleProducts={sampleProducts}
                        sampleBundles={sampleBundles}
                    />
                </PivotItem>
                <PivotItem itemKey='cart' headerText='Cart'>
                    <StorefrontCartPageEditor
                        globalSettings={gSettings.data}
                        globalTheme={gTheme}
                        storefrontSettings={sSettings}
                        storefrontTheme={sTheme}
                        editMode={effectiveEditMode}
                        onStorefrontSettingsChange={(newSettings) => setSSettings(newSettings)}
                        storefrontHeaderLinks={sfHeaderLinks.data?.items ?? []}
                        globalHeaderLinks={globalHeaderLinks.data?.items ?? []}
                        onHeaderLinksChange={noop}
                        storefrontFooterLinks={sfFooterLinks.data?.items ?? []}
                        globalFooterLinks={globalFooterLinks.data?.items ?? []}
                        onFooterLinksChange={noop}
                        sampleProducts={sampleProducts}
                        sampleBundles={sampleBundles}
                    />
                </PivotItem>
                <PivotItem itemKey='checkout' headerText='Checkout'>
                    <StorefrontCheckoutPageEditor
                        globalSettings={gSettings.data}
                        globalTheme={gTheme}
                        storefrontSettings={sSettings}
                        storefrontTheme={sTheme}
                        editMode={effectiveEditMode}
                        onStorefrontSettingsChange={(newSettings) => setSSettings(newSettings)}
                        storefrontHeaderLinks={sfHeaderLinks.data?.items ?? []}
                        globalHeaderLinks={globalHeaderLinks.data?.items ?? []}
                        onHeaderLinksChange={noop}
                        storefrontFooterLinks={sfFooterLinks.data?.items ?? []}
                        globalFooterLinks={globalFooterLinks.data?.items ?? []}
                        onFooterLinksChange={noop}
                        sampleProducts={sampleProducts}
                        sampleBundles={sampleBundles}
                    />
                </PivotItem>
                <PivotItem itemKey='login' headerText='Sign In'>
                    {/* No header/footer props: the runtime's auth pages render neither. */}
                    <StorefrontLoginPageEditor
                        globalSettings={gSettings.data}
                        globalTheme={gTheme}
                        storefrontSettings={sSettings}
                        storefrontTheme={sTheme}
                        editMode={editMode}
                        onStorefrontSettingsChange={(newSettings) => setSSettings(newSettings)}
                    />
                </PivotItem>
                <PivotItem itemKey='headerFooter' headerText='Header & Footer'>
                    <StorefrontHeaderFooterEditor
                        globalSettings={gSettings.data}
                        globalTheme={gTheme}
                        storefrontSettings={sSettings}
                        storefrontTheme={sTheme}
                        editMode={effectiveEditMode}
                        onStorefrontSettingsChange={(newSettings) => setSSettings(newSettings)}
                        storefrontHeaderLinks={sfHeaderLinks.data?.items ?? []}
                        globalHeaderLinks={globalHeaderLinks.data?.items ?? []}
                        onHeaderLinksChange={noop}
                        storefrontFooterLinks={sfFooterLinks.data?.items ?? []}
                        globalFooterLinks={globalFooterLinks.data?.items ?? []}
                        onFooterLinksChange={noop}
                    />
                </PivotItem>
            </Pivot>
            {pagesMenuOpen && (
                <ContextualMenu
                    target={`#${homeChevronId}`}
                    items={pagesMenuItems}
                    onDismiss={() => setPagesMenuOpen(false)}
                />
            )}
            <StorefrontManagePagesPanel
                isOpen={managePagesPanel.isOpen}
                onDismiss={() => managePagesPanel.close()}
                storefront={storefront}
                pages={customPages}
                selectedPageId={selectedPageId}
                onSelectPage={setSelectedPageId}
                onDeletePage={handleDeletePage}
            />
            {canAdminStyles && (
                <StorefrontStylesPanel
                    isOpen={stylesPanelOpen}
                    onDismiss={() => setStylesPanelOpen(false)}
                    storefront={storefront}
                    styles={serverStyles}
                    isLoading={sStyles.isLoading}
                    onDraftChange={setDraftStyle}
                    usageScanRoots={styleUsageRoots}
                    styleBulkApplyPlan={planStyleBulkApply}
                    onApplyStyleToAll={applyStyleToAllElements}
                    // Both the scan source and one of the write targets for the palette editor's
                    // "Scan storefront colors" helper - a working-settings draft edit like the
                    // bulk apply above, so it persists on Exit Edit Mode rather than saving itself.
                    workingSettings={sSettings}
                    onWorkingSettingsChange={setSSettings}
                    defaultStyleIds={getDefaultStyleIds(sSettings)}
                    onSetDefaultStyle={(elementType, styleId) => {
                        // A working-settings draft edit like any other — persists on save/publish.
                        if (sSettings) setSSettings(setDefaultStyleId(sSettings, elementType, styleId));
                    }}
                />
            )}
        </div>
        </StorefrontStylesProvider>
        </AssetSourceProvider>
    );
};

export default StorefrontPagesEditorPage;
